import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { useTranslate } from 'react-admin';
import type { AuditLogEntry } from '@/api/models';
import { formatMinorUnits } from '@/utils/amount';
import { isAmountMetadataKey, isBooleanMetadataKey, whitelistedMetadataEntries } from './domain';

interface AuditEntryDetailDialogProps {
  entry: AuditLogEntry | undefined;
  onClose: () => void;
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, textAlign: 'right', fontFamily: mono ? (theme) => theme.custom.fontFamilyMono : undefined }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Full-record detail for one audit-log row: the fixed columns plus its
 * metadata, rendered as structured key/value — never a raw JSON dump. The
 * contract's list response already carries the full `metadata` object on
 * every item (there is no separate `GET /v1/admin/audit-log/{id}`), so this
 * is a presentation-only dialog over data already fetched, not a second
 * request.
 *
 * Metadata rendering is whitelist-driven (`whitelistedMetadataEntries`):
 * only the keys the catalog documents for this row's `action` are ever
 * shown, in the catalog's own order. This is deliberate defense in depth —
 * the audit log is masked server-side already, but this screen never trusts
 * that alone and never iterates `Object.keys(entry.metadata)` directly.
 */
export function AuditEntryDetailDialog({ entry, onClose }: AuditEntryDetailDialogProps) {
  const translate = useTranslate();

  if (!entry) return null;

  const currentEntry = entry;
  const metadataEntries = whitelistedMetadataEntries(currentEntry.action, currentEntry.metadata);

  function formatMetadataValue(key: string, value: string): string {
    if (isBooleanMetadataKey(key)) {
      if (value === 'true') return translate('pay.audit.detail.booleanYes');
      if (value === 'false') return translate('pay.audit.detail.booleanNo');
      return value;
    }
    if (isAmountMetadataKey(key)) {
      const parsed = Number(value);
      if (Number.isInteger(parsed) && currentEntry.metadata.currency) {
        return formatMinorUnits(parsed, currentEntry.metadata.currency);
      }
    }
    return value;
  }

  return (
    <Dialog open={Boolean(entry)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate('pay.audit.detail.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Field label={translate('pay.audit.detail.fieldId')} value={entry.id} mono />
          <Field label={translate('pay.audit.detail.fieldAt')} value={new Date(entry.at).toLocaleString('es-ES')} />
          <Divider />
          <Field label={translate('pay.audit.detail.fieldAction')} value={translate(`pay.audit.action.${entry.action}`, { _: entry.action })} />
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {translate('pay.audit.detail.fieldActor')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={translate(`pay.audit.actorType.${entry.actorType}`, { _: entry.actorType })} />
              {/* «Nombre (id)» when a display name was captured at write time; the raw id alone otherwise. The id always stays visible, in mono. */}
              {entry.actorDisplay && <Typography variant="body2" sx={{ fontWeight: 500 }}>{entry.actorDisplay}</Typography>}
              <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: (theme) => theme.custom.fontFamilyMono }}>
                {entry.actorDisplay ? `(${entry.actor})` : entry.actor}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {translate('pay.audit.detail.fieldObjectType')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={translate(`pay.audit.objectType.${entry.objectType}`, { _: entry.objectType })} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontFamily: (theme) => theme.custom.fontFamilyMono }}
              >
                {entry.objectId}
              </Typography>
            </Stack>
          </Stack>
          <Field
            label={translate('pay.audit.detail.fieldTenant')}
            value={
              entry.tenantCode
                ? entry.tenantName
                  ? `${entry.tenantName} (${entry.tenantCode})`
                  : entry.tenantCode
                : translate('pay.audit.detail.tenantPlatformWide')
            }
          />
          <Field label={translate('pay.audit.detail.fieldIp')} value={entry.ip ?? translate('pay.audit.detail.ipNotAvailable')} mono />

          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {translate('pay.audit.detail.metadataTitle')}
          </Typography>
          {metadataEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {translate('pay.audit.detail.metadataEmpty')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {metadataEntries.map(([key, value]) => (
                <Field
                  key={key}
                  label={translate(`pay.audit.metadataKey.${key}`, { _: key })}
                  value={formatMetadataValue(key, value)}
                  mono={key.endsWith('_id') || key === 'fingerprint' || key === 'filters_hash' || key === 'token_fingerprint'}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{translate('pay.audit.detail.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
